<script lang="tsx">
import { type AxiosResponse } from 'axios'
import {
  type ComputedRef, type PropType, type Ref,
  computed,
  defineComponent,
  onBeforeMount, onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import useClickOutside from '../../composables/use-click-outside'
import type ApiInterface from '../../types/api-interface'
import ARKCheckbox from '../checkbox/ARKCheckbox.vue'

/**
 * Компонент ARKPhotoGallery представляет собой гибкую и настраиваемую галерею изображений.
 * Он обеспечивает возможность просмотра, масштабирования, поворота и управления изображениями,
 * а также предоставляет пользовательские слоты для интеграции пользовательских элементов интерфейса.
 * <ul>
 * <li><a href="https://www.figma.com/file/Q4Z4CnFx4i0qhD5fbgnPpf/%F0%9F%92%BB-%D0%94%D0%B8%D0%B7%D0%B0%D0%B9%D0%BD-%D1%81%D0%B8%D1%81%D1%82%D0%B5%D0%BC%D0%B0-ARK-SPACE?type=design&node-id=6523%3A96924&mode=dev" target="_blank">Дизайн</a></li>
 * </ul>
 */
export default defineComponent({
  name: 'ARKPhotoGallery',

  props: {
    /**
     * Отображает панель со всеми слайдами
     */
    isShowSlider: {
      type: Boolean,
      default: true,
    },

    /**
     * Отображает панель с кнопками управления (масштаб, поворот и пр.)
     */
    isShowControls: {
      type: Boolean,
      default: true,
    },

    /**
     * Отображает номер текущего слайда (например: "2 из 7")
     */
    isShowPagination: {
      type: Boolean,
      default: true,
    },

    /**
     * Отображает чекбоксы на слайдах (и возможность их выбрать) в панели слайдов (isShowSlider)
     */
    isWithCheckboxes: {
      type: Boolean,
      default: false,
    },

    /**
     * Функция, получающая текущий слайд (отображаемый на экране), и
     * возвращающая JSX-элемент, представляющий содержимое заголовка галереи
     */
    headerSlot: {
      type: Function as PropType<(currentImage: any) => JSX.Element>,
    },

    /**
     * Флаг, позволяющий скрывать кнопку скачивания файла из панели кнопок управления
     */
    hideDownloadButton: {
      type: Boolean,
      default: false,
    },

    /**
     * Функция для добавления кастомного элемента на панель с кнопками управления (isShowControls)
     */
    controlsSlot: {
      type: Function as PropType<(currentImage: any) => JSX.Element>,
    },

    /**
     * Функция для добавления кастомного элемента к каждому слайду на панели слайдов (isShowSlider)
     */
    sliderImageSlot: {
      type: Function as PropType<(image: any) => JSX.Element>,
    },

    /**
     * Массив объектов-изображений
     */
    images: {
      type: Array as PropType<any[]>,
      required: true,
    },

    /**
     * Начальный индекс слайда, который будет отображаться при первоначальном открытии галереи
     */
    startIndex: {
      type: Number,
      default: 0,
    },

    /**
     * API-клиент для выполнения HTTP-запросов
     */
    axios: {
      type: Object as PropType<ApiInterface>,
      required: true,
    },

    /**
     * Функция обработки выбранных изображений
     * получает массив изображений, выбранных чекбоксами (isWithCheckboxes)
     */
    onCheck: {
      type: Function as PropType<(images: any[]) => void>,
    },

    /**
     * Функция формирования имени файла для скачивания
     */
    getFileName: {
      type: Function as PropType<(image: any) => string>,
      default: (image: any) => `${image.name}.${image.extension}`,
    },

    /**
     * Функция, вызываемая при закрытии галереи
     */
    closeAction: {
      type: Function as PropType<() => void>,
      required: true,
    },
  },

  setup(props, { slots }) {
    let isDragging = false
    const isLeftShadowActive: Ref<boolean> = ref(false)
    const isRightShadowActive: Ref<boolean> = ref(false)

    const index: Ref<number> = ref(
      props.startIndex >= props.images.length
        ? props.images.length - 1
        : props.startIndex,
    )
    const checkedImages: any[] = reactive([])
    const currentImage: ComputedRef<any> = computed(() => props.images[index.value])

    let dragX = 0
    let dragY = 0
    const angle: Ref<number> = ref(0)
    const scale: Ref<number> = ref(1)
    const scalePercent: ComputedRef<number> = computed(() => Math.floor(scale.value * 100))
    const translateX: Ref<number> = ref(0)
    const translateY: Ref<number> = ref(0)

    const root: Ref<HTMLDivElement | null> = ref(null)
    const header: Ref<HTMLDivElement | null> = ref(null)
    const prevBtn: Ref<HTMLDivElement | null> = ref(null)
    const nextBtn: Ref<HTMLDivElement | null> = ref(null)
    const slider: Ref<HTMLDivElement | null> = ref(null)
    const rotates: Ref<HTMLDivElement | null> = ref(null)
    const size: Ref<HTMLDivElement | null> = ref(null)
    const downloadEl: Ref<HTMLDivElement | null> = ref(null)
    const image: Ref<HTMLDivElement | null> = ref(null)
    const pagination: Ref<HTMLDivElement | null> = ref(null)

    const scrollToImage = (noAnimation = false) => {
      if (!index.value && index.value !== 0) return
      if (!slider.value) return

      const target = (slider.value.children[0]?.children[index.value] as HTMLDivElement) || undefined
      if (target) {
        slider.value.scrollTo({
          behavior: noAnimation ? undefined : 'smooth',
          top: target.getBoundingClientRect().top + slider.value.scrollTop - (slider.value.clientHeight / 2) + (target.clientHeight / 2),
        })
      }
    }
    watch(index, () => scrollToImage())

    const toggleIsDragging = (val: boolean) => {
      isDragging = val
    }

    const toggleShadow = (val: boolean, isRight = false) => {
      if (isRight) {
        isRightShadowActive.value = val
      } else {
        isLeftShadowActive.value = val
      }
    }

    const resetScale = () => {
      angle.value = 0
      scale.value = 1
      translateX.value = 0
      translateY.value = 0
    }
    const prev = () => {
      if (index.value === 0) {
        index.value = props.images.length - 1
      } else {
        index.value -= 1
      }

      resetScale()
    }

    const next = () => {
      if (index.value === props.images.length - 1) {
        index.value = 0
      } else {
        index.value += 1
      }

      resetScale()
    }

    const rotateImg = (degrease: number) => {
      angle.value += degrease
    }

    const scaleImg = (val: number) => {
      const minScale = 0.1
      const newScale: number = scale.value + val

      scale.value = newScale <= minScale ? minScale : newScale
    }

    const setImage = (image: any) => {
      index.value = props.images.indexOf(image)
      resetScale()
    }

    const download = async (image: any) => {
      const response: AxiosResponse<Blob> = await props.axios.get(image.file, { responseType: 'blob' })

      const link: HTMLAnchorElement = document.createElement('a')
      link.href = window.URL.createObjectURL(response.data)
      link.setAttribute('download', props.getFileName(image))

      document.body.appendChild(link)
      link.click()
      link.remove()
    }

    const isImageChecked = (image: any) => checkedImages.indexOf(image) > -1

    const toggleImageChecked = (image: any) => {
      if (isImageChecked(image)) {
        checkedImages.splice(checkedImages.indexOf(image), 1)
      } else {
        checkedImages.push(image)
      }

      if (props.onCheck) {
        props.onCheck([...checkedImages])
      }
    }

    const dragStartHandler = (e: MouseEvent) => {
      toggleIsDragging(true)

      dragX = e.screenX
      dragY = e.screenY
    }

    const draggingHandler = (e: MouseEvent) => {
      if (!isDragging) return

      translateX.value -= (dragX - e.screenX) / scale.value
      translateY.value -= (dragY - e.screenY) / scale.value
      dragX = e.screenX
      dragY = e.screenY
    }

    const keyDownHandler = (e: KeyboardEvent) => {
      if (!e.repeat) {
        if (e.key === 'ArrowLeft') prev()
        if (e.key === 'ArrowRight') next()
      }
    }

    const wheelHandler = (e: WheelEvent) => {
      const deltaScale: number = e.deltaY > 0 ? -0.1 : 0.1

      scaleImg(deltaScale)
    }

    useClickOutside([header, prevBtn, nextBtn, slider, rotates, size, downloadEl, image, pagination], props.closeAction, root)

    onMounted(() => {
      scrollToImage(true)
    })

    onBeforeMount(() => {
      document.addEventListener('keydown', keyDownHandler)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('keydown', keyDownHandler)
    })

    return () => (

      <div ref={root} onMouseup={() => toggleIsDragging(false)} onMousemove={draggingHandler} class="ark-ui-photo-gallery">

        <div class={`ark-ui-photo-gallery-shadow ark-ui-photo-gallery-shadow-left ${isLeftShadowActive.value ? 'active' : ''}`} />
        <div class={`ark-ui-photo-gallery-shadow ark-ui-photo-gallery-shadow-right ${isRightShadowActive.value ? 'active' : ''}`} />

        { props.headerSlot && (

          <div ref={header} class="ark-ui-photo-gallery-header">
            <div>{ props.headerSlot(currentImage.value) }</div>
          </div>

        )}
        <div onClick={() => props.closeAction()} class="ark-ui-photo-gallery-button ark-ui-photo-gallery-button-exit" />

        { props.isShowPagination && props.images.length > 1 && (
          <div ref={pagination} class="ark-ui-photo-gallery-pagination">
            { index.value + 1 } из { props.images.length }
          </div>
        )}

        <div class="ark-ui-photo-gallery-body">

          {props.images.length > 1 && (
              <div
              ref={prevBtn}
              onClick={() => prev()}
              onMouseenter={() => toggleShadow(true)}
              onMouseleave={() => toggleShadow(false)}

              class="ark-ui-photo-gallery-button-container prev"
            >
              <div class="ark-ui-photo-gallery-button ark-ui-photo-gallery-button-prev"/>
            </div>
          )}

          <img
            ref={image}
            src={currentImage.value.file}
            style={`transform: scale(${scale.value}) translate(${translateX.value}px, ${translateY.value}px) rotate(${angle.value}deg)`}
            onMousedown={dragStartHandler}
            onWheel={wheelHandler}
            onDragstart={(e) => e.preventDefault()}

            class="ark-ui-photo-gallery-image"
            alt="Current image"
            draggable
          />

          {props.images.length > 1 && (
            <div
              ref={nextBtn}
              onClick={() => next()}
              onMouseenter={() => toggleShadow(true, true)}
              onMouseleave={() => toggleShadow(false, true)}

              class="ark-ui-photo-gallery-button-container next"
            >
              <div class="ark-ui-photo-gallery-button ark-ui-photo-gallery-button-next" />
            </div>
          )}

          { props.isShowSlider && props.images.length > 1 && (

            <div ref={slider} class="ark-ui-photo-gallery-slider">
              <div class="ark-ui-photo-gallery-slider-wrapper">
                { props.images.map((item) => (

                  <div
                    onClick={(e: MouseEvent) => setImage(item)}
                    class={`ark-ui-photo-gallery-slider-wrapper-item ${item.id === currentImage.value.id ? 'active' : ''}`}
                  >
                    <img src={item.file} alt="Slider item" />

                    { props.sliderImageSlot && props.sliderImageSlot(item) }

                    { props.isWithCheckboxes && (

                      <div class="ark-ui-photo-gallery-slider-wrapper-checkbox">
                        <ARKCheckbox modelValue={isImageChecked(item)} onValueChange={() => toggleImageChecked(item)} />
                      </div>

                    ) }
                  </div>

                )) }
              </div>
            </div>

          ) }

        </div>

        { props.isShowControls && (

          <div class="ark-ui-photo-gallery-footer">
            <div ref={rotates} class="ark-ui-photo-gallery-rotates">

              <div onClick={() => rotateImg(-90)} class="ark-ui-photo-gallery-button ark-ui-photo-gallery-button-rotate-left" />
              <div onClick={() => rotateImg(90)} class="ark-ui-photo-gallery-button ark-ui-photo-gallery-button-rotate-right" />

            </div>

            <div ref={size} class="ark-ui-photo-gallery-size" style={props.hideDownloadButton ? 'margin-right: 0;' : ''}>

              <div onClick={() => scaleImg(-0.1)} class="ark-ui-photo-gallery-button ark-ui-photo-gallery-button-minus" />
              { scalePercent.value }%
              <div onClick={() => scaleImg(0.1)} class="ark-ui-photo-gallery-button ark-ui-photo-gallery-button-plus" />

            </div>

            {!props.hideDownloadButton && (
              <div
                ref={downloadEl}
                class="ark-ui-photo-gallery-button ark-ui-photo-gallery-button-download"
                onClick={() => download(currentImage.value)}
              />
            )}

            { props.controlsSlot && props.controlsSlot(currentImage.value) }
          </div>

        ) }
      </div>

    )
  },
})
</script>

<style lang="scss">
.ark-ui-photo-gallery {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  user-select: none;
  background: rgba(88, 91, 105, 0.8);
  z-index: $popup;

  &-header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    background: rgba(39, 40, 46, 0.7);
    padding: 14px 24px;
    padding-right: 24px + 32px + 24px;
    height: 32px;
    z-index: $element;
  }

  &-pagination {
    width: max-content;
    height: max-content;
    padding: 2px 8px;

    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: 40px;
    z-index: $tooltip;

    background: rgba(39, 40, 46, 0.7);
    border-radius: 4px;

    color: #fff;
    text-align: center;
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
  }

  &-body {
    display: flex;
    align-items: center;
    justify-content: center;

    width: 100%;
    height: 100%;

    & > img {
      max-height: 97vh;
      max-width: 97vw;
      border-radius: 8px;
      cursor: grab;
    }

    .ark-ui-photo-gallery-button-container {
      position: absolute;
      top: 0;
      z-index: $element;

      height: 100%;

      display: flex;
      align-items: center;

      cursor: pointer;
      pointer-events: all;
      div {
        pointer-events: none;
      }

      &.next {
        padding: 0 24px 0 50px;
        right: 0;
        justify-content: flex-start;
      }
      &.prev {
        padding: 0 50px 0 24px;
        left: 0;
        justify-content: flex-end;
      }

      &:hover {
        div {
          background-color: #27282E;
        }
      }
    }
  }

  &-slider {
    position: absolute;
    right: 10%;
    top: 0;

    width: max-content;
    height: 100vh;

    overflow: auto;
    scrollbar-width: none !important;
    cursor: ns-resize;

    &::-webkit-scrollbar {
      appearance: none;
      display: none;
    }

    &-wrapper {
      width: max-content;
      height: max-content;
      padding: calc(50vh - 32px) 8px;

      display: flex;
      align-items: center;
      flex-direction: column;
      gap: 8px;

      &-item {
        width: 64px;
        height: 64px;
        cursor: pointer;
        position: relative;

        overflow: hidden;
        border-radius: 8px;
        border: 1px solid transparent;

        & > img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: $negative;
          filter: brightness(.5) saturate(.5);
          transition: all .3s;

          &:hover {
            transition: all .1s;
            filter: none;
          }
        }

        &.active {
          border-color: #FFFFFF;
          img {
            filter: none;
          }
        }
      }

      &-checkbox {
        position: absolute;
        top: 0;
        left: 0;
        z-index: $element;
      }
    }
  }

  &-footer {
    width: 100%;
    display: flex;
    justify-content: center;

    bottom: 5%;
    position: absolute;
  }

  &-rotates {
    display: flex;
    margin-right: 42px;
  }

  &-size {
    display: flex;
    align-items: center;
    justify-content: space-between;

    width: 124px;
    border-radius: 4px;
    margin-right: 82px;
    background-color: rgba(39, 40, 46, 0.7);

    font-size: 14px;
    font-weight: 400;
    color: #fff;

    .ark-ui-photo-gallery-button {
      background-color: transparent;
    }
  }

  &-shadow {
    position: absolute;
    top: 0;
    width: 15%;
    height: 100%;
    opacity: .7;
    z-index: $element;
    pointer-events: none;

    &-left {
      left: 0;

      &.active {
        background: linear-gradient(90deg, #27282E 0%, #27282E 0.01%, rgba(39, 40, 46, 0) 100%);
      }
    }

    &-right {
      right: 0;

      &.active {
        background: linear-gradient(-90deg, #27282E 0%, #27282E 0.01%, rgba(39, 40, 46, 0) 100%);
      }
    }
  }

  &-button {
    width: 32px;
    min-width: 32px;
    height: 32px;

    cursor: pointer;
    border-radius: 4px;

    background-color: rgba(39, 40, 46, 0.7);
    background-position: center;
    background-repeat: no-repeat;

    &-prev, &-next {
      background-image: url('./assets/arrow.svg');
      background-repeat: no-repeat;
    }

    &-prev {
      z-index: $element;
    }

    &-next {
      transform: rotate(180deg);
    }

    &-rotate-left, &-rotate-right {
      background-image: url('./assets/rotate.svg');
      background-repeat: no-repeat;
    }

    &-rotate-left {
      margin-right: 8px;
    }

    &-rotate-right {
      transform: rotateY(180deg);
    }

    &-minus {
      background-image: url('./assets/minus.svg');
      background-repeat: no-repeat;
    }

    &-plus {
      background-image: url('./assets/plus.svg');
      background-repeat: no-repeat;
    }

    &-download {
      background-image: url('./assets/download.svg');
      background-repeat: no-repeat;
    }

    &-exit {
      background-image: url('./assets/exit.svg');
      background-repeat: no-repeat;
      position: absolute;
      right: 24px;
      top: 14px;
      z-index: $menu;
    }

    &:hover {
      background-color: #27282E;
    }
  }
}
</style>
